import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { PopoverController, PopoverPosition } from "@amzn/stencil-react-components/dist/submodules/popover";
import { Input } from "@amzn/stencil-react-components/input";
import { Col, Spacer } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { Text } from "@amzn/stencil-react-components/text";
import { IconBookmarkSmall } from "@amzn/stencil-react-icons";
import { TaxonomyIdItem } from "@amzn/taxonomy-lib";
import { getString } from "common/uistringlabels/uiStringUtils";
import { useLists } from "hooks/ListsContext";
import { ListModel } from "lib/models/list-models";
import React, { FC, useRef, useState } from "react";

import { useMessageBannersBox } from "../MessageBannersBox/hook";

export interface ListActionsProps {
  taxonomyId: string;
  lists: ListModel[];
  allowedDomain: string;
}

export const ListActions: FC<ListActionsProps> = ({ taxonomyId, lists, allowedDomain }) => {
  const { addItemToList, createList } = useLists();
  const [isOpen, setIsOpen] = useState(false);
  const [newListName, setNewListName] = useState("");
  const target = useRef(null);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen(!isOpen);
  const { addMessageBanner } = useMessageBannersBox();
  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    try {
      await createList({
        name: newListName,
        allowedDomains: [allowedDomain],
        itemIds: [taxonomyId],
      });
      addMessageBanner(getString("lists.modal.updateSuccess"), MessageBannerType.Success);
      setNewListName("");
      close();
    } catch (error) {
      addMessageBanner(getString("lists.modal.updateError"), MessageBannerType.Error);
    }
  };

  const addToList = async (listId: string) => {
    try {
      await addItemToList([taxonomyId], { listId });
      addMessageBanner(getString("lists.modal.updateSuccess"), MessageBannerType.Success);
    } catch (error) {
      addMessageBanner(getString("lists.modal.updateError"), MessageBannerType.Error);
    }
  };

  return (
    <>
      <Button
        icon={<IconBookmarkSmall />}
        variant={ButtonVariant.Tertiary}
        onClick={(e) => {
          toggle();
          e.preventDefault();
          e.stopPropagation();
        }}
        style={{ maxHeight: "40px" }}
        ref={target}
        dataTestId="bookmark-button"
      />
      <PopoverController target={target} close={close} isOpen={isOpen} position={PopoverPosition.BottomCenter}>
        <Col
          padding="S300"
          gridGap="S300"
          alignItems="flex-start"
          minWidth="180px"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          dataTestId="popover-content"
        >
          {lists &&
            lists.length > 0 &&
            lists.map(
              (list, index) =>
                list.allowedDomains.includes(allowedDomain) && (
                  <Button
                    variant={ButtonVariant.Secondary}
                    style={{ width: "100%", textAlign: "left" }}
                    key={`List-${index}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      addToList(list.listId);
                    }}
                  >
                    {list.name}
                  </Button>
                )
            )}
          {(!lists || lists.length === 0) && <Text>{getString("lists.noLists")}</Text>}
          <Spacer height="500" />
          <Col gridGap="S250">
            <Input
              placeholder={getString("lists.newList")}
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
            />
            <Button variant={ButtonVariant.Secondary} onClick={handleCreateList} style={{ alignSelf: "center" }}>
              {getString("lists.createList")}
            </Button>
          </Col>
        </Col>
      </PopoverController>
    </>
  );
};
